import {FC, useState} from 'react';
import {toast} from 'react-toastify';
import {Navigate, useLocation, useNavigate, useParams} from 'react-router-dom';
import {deleteBrand, updateOrganization, VerdocsEndpoint, type IBrand} from '@verdocs/js-sdk';
import {ArrowLeftIcon, ChevronRightIcon, EllipsisVerticalIcon, PaintBrushIcon, PencilIcon, SparklesIcon} from '@heroicons/react/24/outline';
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from '../../Components/DropdownMenu';
import {invalidateAllQueries, useBrand, useBrands, useOrganization} from '../../react-sdk/queries';
import {FlexFill, HelpDialog, PageTitle, Spinner} from '../../Components';
import {StartFromThemeDialog} from '../../Dialogs/StartFromThemeDialog';
import {MatchWebsiteDialog} from '../../Dialogs/MatchWebsiteDialog';
import {CreateBrandDialog} from '../../Dialogs/CreateBrandDialog';
import {OkCancelDialog} from '../../Dialogs/OkCancelDialog';
import {brandInitials, brandTileColor} from './BrandThemes';
import {StatusDot, type TDotTone} from './BrandStatus';
import {DisclosuresEditor} from './DisclosuresEditor';
import {BrandHub, type THubTab} from './BrandHub';
import {BrandBuilder} from './BrandBuilder';
import {BrandPreview} from './BrandPreview';
import {EmailBuilder} from './EmailBuilder';
import {URLS} from '../../Routing/urls';
import {AppState} from '../../AppState';

type TCreatePath = 'match' | 'theme' | 'scratch' | null;

// Everything below a brand lives in the URL hash so a refresh or a shared link lands in the same
// place: the hub's tabs, plus the two builders and the full preview.
type TBrandView = THubTab | 'theme-editor' | 'email-builder' | 'disclosures' | 'preview';
const BRAND_VIEWS: TBrandView[] = [
  'overview',
  'communications',
  'hosting',
  'signing',
  'certification',
  'theme-editor',
  'email-builder',
  'disclosures',
  'preview',
];

const senderStatus = (brand: IBrand): {tone: TDotTone; label: string} => {
  switch (brand.email_domain_status) {
    case 'verified':
      return {tone: 'green', label: 'Sender verified'};
    case 'pending':
      return {tone: 'amber', label: 'Sender pending'};
    case 'failed':
      return {tone: 'amber', label: 'Sender failed'};
    case 'suspended':
      return {tone: 'amber', label: 'Sender suspended'};
    default:
      return {tone: 'muted', label: 'Sender not configured'};
  }
};

const hostingStatus = (brand: IBrand): {tone: TDotTone; label: string} => {
  switch (brand.app_domain_status) {
    case 'active':
      return {tone: 'green', label: 'Hosting active'};
    case 'pending':
      return {tone: 'amber', label: 'Hosting pending'};
    case 'failed':
      return {tone: 'amber', label: 'Hosting failed'};
    case 'suspended':
      return {tone: 'amber', label: 'Hosting suspended'};
    default:
      return {tone: 'muted', label: 'Hosting default'};
  }
};

const BrandRow: FC<{
  brand: IBrand;
  isDefault: boolean;
  onOpen: () => void;
  onToggleDefault: () => void;
  onDelete: () => void;
}> = ({brand, isDefault, onOpen, onToggleDefault, onDelete}) => {
  const logoUrl = brand.thumbnail_url || brand.full_logo_url;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => e.key === 'Enter' && onOpen()}
      className="group flex cursor-pointer items-center gap-3.5 rounded-xl border-[1.5px] border-gray-100 bg-white px-4 py-3.5 transition-all hover:border-indigo-300 hover:shadow-md">
      {logoUrl ? (
        <img src={logoUrl} alt="" className="h-11 w-11 shrink-0 rounded-[11px] bg-gray-50 object-contain" />
      ) : (
        <div
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[11px] text-base font-extrabold text-white"
          style={{background: brandTileColor(brand)}}>
          {brandInitials(brand.name, brand.key)}
        </div>
      )}

      <div className="w-[250px] min-w-0 shrink-0">
        <div className="flex items-center gap-2">
          <b className="truncate text-[14.5px] font-semibold text-gray-900">{brand.name || brand.key}</b>
          <span className="font-mono text-[11px] text-gray-400">{brand.key}</span>
          {isDefault && (
            <span className="whitespace-nowrap rounded-full bg-indigo-50 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wider text-indigo-600">
              Default
            </span>
          )}
        </div>
        <div className="mt-0.5 truncate text-[11.5px] text-gray-400">
          {isDefault ? 'Applies to everything unless overridden' : 'Used when passed per envelope'}
        </div>
      </div>

      <div className="flex flex-1 flex-wrap items-center justify-end gap-x-4 gap-y-1">
        <StatusDot tone="green" label="Published" />
        <StatusDot {...senderStatus(brand)} />
        <StatusDot {...hostingStatus(brand)} />
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            onClick={(e) => e.stopPropagation()}
            className="shrink-0 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            title="Brand actions">
            <EllipsisVerticalIcon className="size-5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-white">
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              onToggleDefault();
            }}>
            {isDefault ? 'Clear default' : 'Set as default'}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="cursor-pointer text-red-600 focus:text-red-600"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}>
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ChevronRightIcon className="size-4 shrink-0 text-gray-300 transition-colors group-hover:text-indigo-500" />
    </div>
  );
};

const BrandPreviewView: FC<{brandId: string; onBack: () => void}> = ({brandId, onBack}) => {
  const organizationId = AppState.currentProfile?.organization_id || '';
  const brandQuery = useBrand(organizationId, brandId);

  return (
    <div className="flex flex-1 flex-col box-border overflow-y-scroll">
      <button onClick={onBack} className="mb-4 flex items-center gap-2 self-start text-sm text-indigo-600 hover:underline">
        <ArrowLeftIcon className="size-4" />
        Back to brand
      </button>
      {brandQuery.data ? (
        <BrandPreview brand={brandQuery.data} />
      ) : (
        <div className="flex flex-1 items-center justify-center">
          <Spinner mode="dark" />
        </div>
      )}
    </div>
  );
};

const PathCard: FC<{icon: FC<{className?: string}>; title: string; blurb: string; onClick: () => void}> = ({icon: Icon, title, blurb, onClick}) => (
  <button
    onClick={onClick}
    className="rounded-2xl border-[1.5px] border-gray-200 bg-white px-4 py-6 text-center transition-all hover:-translate-y-0.5 hover:border-indigo-300 hover:shadow-lg">
    <div className="mx-auto mb-3.5 flex h-[52px] w-[52px] items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
      <Icon className="size-6" />
    </div>
    <h3 className="mb-1.5 text-[15px] font-semibold text-gray-900">{title}</h3>
    <p className="text-xs leading-relaxed text-gray-500">{blurb}</p>
  </button>
);

export const Branding: FC = () => {
  const organizationId = AppState.currentProfile?.organization_id || '';
  const brandsQuery = useBrands(organizationId);
  const orgQuery = useOrganization(organizationId);
  const {brandKey} = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Set when a builder hands the user back to the hub with a developer panel to open.
  const [devPanelRequest, setDevPanelRequest] = useState<string | null>(null);
  const [creating, setCreating] = useState<TCreatePath>(null);
  const [deletingBrand, setDeletingBrand] = useState<IBrand | null>(null);
  const [working, setWorking] = useState(false);

  const brands = brandsQuery.data || [];
  const defaultBrandId = orgQuery.data?.default_brand_id;

  // Navigation: list -> hub (the brand's tabs) -> the Theme Editor, the Email Builder, or the
  // full preview, each handing the user back to the hub.
  if (brandKey) {
    const brand = brands.find((b) => b.key === brandKey);
    if (!brand) {
      // A brand created a moment ago is not in the list until the refetch lands.
      return brandsQuery.isLoading || brandsQuery.isFetching ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner mode="dark" />
        </div>
      ) : (
        <Navigate to={URLS.SETTINGS_BRANDING.build()} replace={true} />
      );
    }

    const hash = location.hash.replace('#', '') as TBrandView;
    const view: TBrandView = BRAND_VIEWS.includes(hash) ? hash : 'overview';
    const go = (next: TBrandView) => navigate(URLS.SETTINGS_BRAND.build(brand.key, next === 'overview' ? undefined : next));

    if (view === 'preview') {
      return <BrandPreviewView brandId={brand.id} onBack={() => go('overview')} />;
    }

    if (view === 'theme-editor') {
      return <BrandBuilder brandId={brand.id} onBack={() => go('overview')} />;
    }

    if (view === 'disclosures') {
      return <DisclosuresEditor brandId={brand.id} onBack={() => go('signing')} />;
    }

    if (view === 'email-builder') {
      return (
        <EmailBuilder
          brandId={brand.id}
          onBack={() => go('overview')}
          onOpenDevPanel={() => {
            setDevPanelRequest('sending');
            go('overview');
          }}
        />
      );
    }

    return (
      <BrandHub
        brandId={brand.id}
        tab={view}
        onTabChange={go}
        openDevPanel={devPanelRequest}
        onDevPanelHandled={() => setDevPanelRequest(null)}
        onBack={() => navigate(URLS.SETTINGS_BRANDING.build())}
        onOpenBuilder={() => go('theme-editor')}
        onOpenEmailBuilder={() => go('email-builder')}
        onOpenDisclosures={() => go('disclosures')}
        onOpenPreview={() => go('preview')}
      />
    );
  }

  const handleToggleDefault = async (brand: IBrand) => {
    const newDefault = defaultBrandId === brand.id ? null : brand.id;
    setWorking(true);
    try {
      await updateOrganization(VerdocsEndpoint.getDefault(), organizationId, {default_brand_id: newDefault} as any);
      invalidateAllQueries();
      toast.success(newDefault ? `${brand.name || brand.key} set as default` : 'Default brand cleared');
    } catch (e: any) {
      toast.error(`Error updating default brand: ${e.message}`);
    }
    setWorking(false);
  };

  const handleDelete = async () => {
    if (!deletingBrand) return;
    setWorking(true);
    try {
      await deleteBrand(VerdocsEndpoint.getDefault(), organizationId, deletingBrand.id);
      invalidateAllQueries();
      toast.success('Brand deleted');
    } catch (e: any) {
      toast.error(`Error deleting brand: ${e.response?.data?.error || e.message}`);
    }
    setDeletingBrand(null);
    setWorking(false);
  };

  const handleCreated = (brand: IBrand) => {
    setCreating(null);
    invalidateAllQueries();
    navigate(URLS.SETTINGS_BRAND.build(brand.key));
  };

  return (
    <div className="flex flex-1 flex-col box-border overflow-y-scroll">
      <PageTitle>
        Brands
        <FlexFill />
        <HelpDialog title="Brands">
          <p className="mb-4">
            Brands control how everything you send looks to the people who receive it. Your organization's default brand applies to everything
            it sends; additional brands can be applied per envelope with <code>brand_key</code>.
          </p>
          <p>Each brand can have its own logos, colors, custom email domain, and CSS overrides to fully white-label the signing experience.</p>
        </HelpDialog>
      </PageTitle>

      <div className="w-full max-w-[980px]">
        <p className="mb-8 max-w-[600px] text-sm leading-relaxed text-gray-500">
          Brands control how everything you send looks to the people who receive it.
        </p>

        {brandsQuery.isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Spinner mode="dark" />
          </div>
        ) : (
          brands.length > 0 && (
            <>
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-700">Your Brands</h2>
              <div className="mb-10 flex flex-col gap-3">
                {brands.map((brand) => (
                  <BrandRow
                    key={brand.id}
                    brand={brand}
                    isDefault={defaultBrandId === brand.id}
                    onOpen={() => navigate(URLS.SETTINGS_BRAND.build(brand.key))}
                    onToggleDefault={() => handleToggleDefault(brand)}
                    onDelete={() => setDeletingBrand(brand)}
                  />
                ))}
              </div>
            </>
          )
        )}

        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-700">Create a Brand</h2>
        {!brandsQuery.isLoading && brands.length === 0 && (
          <p className="mb-4 max-w-[640px] text-sm text-gray-500">
            You have no brands yet. Create one to customize logos, colors, email domains, and the signing experience.
          </p>
        )}
        <div className="grid grid-cols-1 gap-4 tablet:grid-cols-3">
          <PathCard
            icon={SparklesIcon}
            title="Match my Web site"
            blurb="Paste your URL and we pull your name, logo, and colors automatically."
            onClick={() => setCreating('match')}
          />
          <PathCard
            icon={PaintBrushIcon}
            title="Start from a theme"
            blurb="Twenty hand-tuned starting points designed for toolbars and buttons, not just palettes."
            onClick={() => setCreating('theme')}
          />
          <PathCard icon={PencilIcon} title="Start from scratch" blurb="Start from a blank canvas." onClick={() => setCreating('scratch')} />
        </div>
      </div>

      {creating === 'scratch' && <CreateBrandDialog onClose={() => setCreating(null)} onCreated={handleCreated} />}
      {creating === 'theme' && <StartFromThemeDialog onClose={() => setCreating(null)} onCreated={handleCreated} />}
      {creating === 'match' && (
        <MatchWebsiteDialog onClose={() => setCreating(null)} onCreated={handleCreated} onSwitchToThemes={() => setCreating('theme')} />
      )}

      {deletingBrand && (
        <OkCancelDialog onOk={handleDelete} onCancel={() => setDeletingBrand(null)}>
          <p className="mb-6">
            Are you sure you want to delete the brand <strong>{deletingBrand.name || deletingBrand.key}</strong>? This cannot be undone.
          </p>
        </OkCancelDialog>
      )}

      {working && (
        <div className="absolute inset-0 flex z-40 bg-[#ffffff7f] items-center justify-center">
          <Spinner mode="dark" />
        </div>
      )}
    </div>
  );
};
